import { Map } from 'immutable'
import { matchPath } from 'react-router'
import {
  Department,
  DepartmentGroup,
  DepartmentGroupIdType,
  DepartmentGroupProps,
  DepartmentIdType,
  DepartmentProps
} from '../types/coreEntitiesTypes'
import {
  departmentGroupIdFromStringId,
  departmentGroupTypeFromGroup,
  getDepartmentGroupId,
  getPlannerPageKeyFromPath,
  isGroupId,
  isGroupTypeAllowedForPlannerPath
} from '../utils/departmentAndDepartmentGroupUtils'
import { ROUTE_LIMIT_DEPARTMENT_GROUP_ID_QUERY_PARAM } from './DepartmentGroupRouteLimitGuardUtils'

export type GuardedDepartmentGroupRouteLimitPageKey = 'planning' | 'routes-overview' | 'live' | 'live2' | 'dispatch'

interface DepartmentGroupRouteLimitPattern {
  path: string
  pageKey: GuardedDepartmentGroupRouteLimitPageKey
}

interface DepartmentGroupRouteLimitRoute {
  isProtected: boolean
  pageKey?: GuardedDepartmentGroupRouteLimitPageKey
}

const DEPARTMENT_GROUP_ROUTE_LIMIT_PATTERNS: DepartmentGroupRouteLimitPattern[] = [
  { path: '/planner/:departmentId/planning', pageKey: 'planning' },
  { path: '/planner/:departmentId/routes-overview', pageKey: 'routes-overview' },
  { path: '/planner/:departmentId/live', pageKey: 'live' },
  { path: '/planner/:departmentId/live2', pageKey: 'live2' },
  { path: '/planner/:departmentId/dispatch', pageKey: 'dispatch' }
] as const

export const accessToDepartmentOrAnyDepartmentsInGroup = (
  departmentOrGroupId: string,
  departments?: Record<DepartmentIdType, DepartmentProps>,
  initialDepartment?: Department,
  departmentGroups?: Record<DepartmentGroupIdType, DepartmentGroupProps>
) => {
  if (isGroupId(departmentOrGroupId)) {
    // biome-ignore lint/style/noNonNullAssertion: isGroupId check on line above
    const departmentGroup = departmentGroups?.[getDepartmentGroupId(departmentOrGroupId)!]
    if (departmentGroup?.departments?.some((departmentId) => !!departments?.[departmentId])) {
      return true
    } else {
      console.error(
        'No access to any departments in the group, or they do not exist.. redirecting',
        departmentOrGroupId
      )
      return false
    }
  } else if (departmentOrGroupId && Number.isSafeInteger(Number(departmentOrGroupId))) {
    const department = departments?.[Number(departmentOrGroupId)]
    if (
      ((departmentOrGroupId && Number(departmentOrGroupId) > 0) || Number.isSafeInteger(Number(departmentOrGroupId))) &&
      !initialDepartment &&
      !department
    ) {
      console.error('No access to department, or it does not exists.. redirecting', departmentOrGroupId)
      return false
    } else {
      return true
    }
  } else if (!departmentOrGroupId) {
    // departmentOrGroupId is not set, so we allow access to page.
    return true
  } else {
    // departmentOrGroupId is neither a group id or a department id, but is set. This is an invalid state, so we redirect.
    console.error('Invalid department or group ID.. redirecting', departmentOrGroupId)
    return false
  }
}

export const plannerGroupRedirectToSelectorUrl = (
  departmentOrGroupId: string | undefined,
  pathname: string,
  search: string,
  departmentGroups: Map<DepartmentGroupIdType, DepartmentGroup>
) => {
  if (!departmentOrGroupId || !isGroupId(departmentOrGroupId)) {
    return undefined
  }

  const departmentGroupId = departmentGroupIdFromStringId(departmentOrGroupId)
  const group = departmentGroupId !== undefined ? departmentGroups.get(departmentGroupId) : undefined
  const groupHasAccess = group ? isGroupTypeAllowedForPlannerPath(departmentGroupTypeFromGroup(group), pathname) : false

  if (groupHasAccess) {
    return undefined
  }

  return plannerRedirectToSelectorUrl(departmentOrGroupId, pathname, search)
}

export const plannerRedirectToSelectorUrl = (
  departmentOrGroupId: string | undefined,
  pathname: string,
  search: string,
  options?: { routeLimitDepartmentGroupId?: string }
) => {
  const plannerPathPrefix = departmentOrGroupId ? `/planner/${departmentOrGroupId}/` : '/planner/'
  const plannerPath = pathname.startsWith(plannerPathPrefix)
    ? pathname.replace(plannerPathPrefix, '')
    : (getPlannerPageKeyFromPath(pathname) ?? '')
  const redirectTo = plannerPath ? `${plannerPath}${search}` : ''
  const query = new URLSearchParams()
  if (redirectTo) {
    query.set('redirect-to', redirectTo)
  }
  if (options?.routeLimitDepartmentGroupId) {
    query.set(ROUTE_LIMIT_DEPARTMENT_GROUP_ID_QUERY_PARAM, options.routeLimitDepartmentGroupId)
  }

  return query.toString() ? `/planner/?${query.toString()}` : '/planner/'
}

export const isRouteGuardedAgainstDepartmentGroupRouteLimit = (pathname: string): DepartmentGroupRouteLimitRoute => {
  for (const pattern of DEPARTMENT_GROUP_ROUTE_LIMIT_PATTERNS) {
    const match = matchPath({ path: pattern.path, end: false }, pathname)
    if (match) {
      return {
        isProtected: true,
        pageKey: pattern.pageKey
      }
    }
  }

  return { isProtected: false }
}

export const departmentGroupRouteLimitRouteFromRedirectTo = (redirectTo?: string): DepartmentGroupRouteLimitRoute => {
  if (!redirectTo) {
    return { isProtected: false }
  }

  const redirectPath = redirectTo.split('?')[0]?.replace(/^\/+/, '')
  return isRouteGuardedAgainstDepartmentGroupRouteLimit(`/planner/selected/${redirectPath}`)
}

const departmentGroupRouteLimitDateFromPath = (pathname: string): string | undefined => {
  const match = pathname.match(/\b\d{4}-\d{2}-\d{2}\b/)
  return match?.[0]
}

export const departmentGroupRouteLimitDateFromRedirectTo = (redirectTo?: string): string | undefined => {
  if (!redirectTo) {
    return undefined
  }

  return departmentGroupRouteLimitDateFromPath(`/planner/selected/${redirectTo}`)
}
